/**
 * CSV Export Utility
 * Provides functions to export data to CSV format
 */

/**
 * Convert array of objects to CSV string
 * @param {Array} data - Array of objects to convert
 * @param {Array} headers - Array of header objects with { key, label } or just strings
 * @returns {string} CSV formatted string
 */
export const arrayToCSV = (data, headers) => {
  if (!data || data.length === 0) {
    return '';
  }

  // Normalize headers to objects
  const normalizedHeaders = headers.map(h => 
    typeof h === 'string' ? { key: h, label: h } : h
  );

  // Create header row
  const headerRow = normalizedHeaders.map(h => `"${h.label}"`).join(',');

  // Create data rows
  const dataRows = data.map(row => {
    return normalizedHeaders.map(h => {
      const value = row[h.key];
      // Handle different value types
      if (value === null || value === undefined) {
        return '""';
      }
      // Convert to string and escape quotes
      const stringValue = String(value).replace(/"/g, '""');
      return `"${stringValue}"`;
    }).join(',');
  });

  return [headerRow, ...dataRows].join('\n');
};

/**
 * Download CSV file
 * @param {string} csvContent - CSV formatted string
 * @param {string} filename - Name of the file to download
 */
export const downloadCSV = (csvContent, filename) => {
  // Add BOM for UTF-8 encoding (helps Excel recognize UTF-8)
  const BOM = '\uFEFF';
  const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  
  if (link.download !== undefined) {
    // Create a link to the file
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }
};

/**
 * Export data to CSV file
 * @param {Array} data - Array of objects to export
 * @param {Array} headers - Array of header objects with { key, label }
 * @param {string} filename - Name of the file to download
 */
export const exportToCSV = (data, headers, filename) => {
  const csvContent = arrayToCSV(data, headers);
  const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
  const filenameWithTimestamp = `${filename}_${timestamp}.csv`;
  downloadCSV(csvContent, filenameWithTimestamp);
};
